<template>
  <section class="main">
    <h1 class="heading">{{ $t('REQUESTS') }}</h1>
    <aside class="filters">
      <requests-filter :filter="filter.filter" :price="filter.price"></requests-filter>
    </aside>
    <div class="apps">
      <p
        v-if="!isLoading && !apps.length"
        style="text-align: center;"
      >{{ $t('NO_REQUESTS_FOUND') }}</p>
      <app
        v-for="app in apps"
        :isRequestable="true"
        type="ios"
        :app="app"
        @tryFulfill="tryFulfill"
        :key="app.id"
        ref="app"
      />
      <loader v-show="!isLastPage"></loader>
    </div>
    <aside class="notifications">
      <div class="notification">
        <h3 class="notification__heading">{{ $t('REQUEST_FOR_APP') }}</h3>
        <html-with-links tag="p" class="notification__text"
                         :html="$t('AUTOMATIC_REQUESTS_TERMS',{'href':'/'})">
        </html-with-links>
        <router-link
          v-if="!$store.getters.isDeviceLinked"
          class="notification__link"
          to="/link"
        >
          {{ $t('LINK_YOUR_DEVICE') }}
        </router-link>
        <button v-else class="notification__link" @click="requestNew">
          {{ $t('REQUEST_APP_TO_BE_UPLOADED') }}
        </button>
      </div>
    </aside>

    <v-popup name="request" class="request-modal" @closed="clearRequestPopup">
      {{ $t('COPY_PASTE_APPSTORE_URL') }}

      <input v-model="requestUrl" class="input" ref="requestUrlInput"/>

      <v-checkbox
        class="request-modal__checkbox"
        v-model="isRequestVersionInputVisible"
      >{{ $t('OLDER_VERSION') }}
      </v-checkbox>

      <template v-if="isRequestVersionInputVisible">
        <div>{{ $t('ENTER_VERSION') }}</div>
        <input v-model="requestVersion" class="input" ref="requestVersionInput"/>
      </template>

      <template #footer>
        <button class="g-button" @click="$modal.hide('request')">
          {{ $t('CANCEL') }}
        </button>
        <button class="g-button g-button_primary" @click="request">
          <loader v-show="requesting" size="13px" border="3px" absolute/>
          {{ $t('OK_BUTTON') }}
        </button>
      </template>
    </v-popup>

    <universal-search-modal
        :request="fulfillingRequest"
        @fulfilled="onFulfilled"
    />
  </section>
</template>

<script>
/* eslint-disable no-param-reassign */
import App from '@/components/App';
import Loader from '@/components/Loader';
import VPopup from '@/components/Popup';
import VCheckbox from '@/components/Checkbox';
import RequestsFilter from '@/components/RequestsFilter';
import { throttle, isEqual } from 'lodash';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';
import UniversalSearchModal from './components/UniversalSearchModal.vue';

export default {
  components: {
    HtmlWithLinks,
    App,
    Loader,
    VPopup,
    VCheckbox,
    RequestsFilter,
    UniversalSearchModal,
  },
  data() {
    return {
      isLoading: false,
      isLastPage: false,
      page: 1,
      apps: [],
      requesting: false,
      isRequestVersionInputVisible: false,
      requestUrl: '',
      requestVersion: '',
      params: { ...this.$route.query },
      fulfillingRequest: null,
      fulfilledRequestsIds: [],
    };
  },
  computed: {
    title() {
      return this.$t('REQUESTS');
    },
    filter() {
      return {
        filter: this.params.filter,
        price: this.params.price,
      };
    },
  },
  watch: {
    isRequestVersionInputVisible(newVal) {
      if (!newVal) {
        return;
      }

      this.$nextTick()
        .then(() => this.$refs.requestVersionInput.focus());
    },
    '$route.query': {
      handler() {
        if (this.$route.name === 'requests' && !isEqual(this.$route.query, this.params)) {
          this.params = { ...this.$route.query };
          this.isLastPage = false;
          this.apps = [];
          this.page = 1;
          this.getApps();
        }
      },
      deep: true,
    },
  },
  methods: {
    mapRequest(app) {
      if (app.price === '0.00') {
        app.price = 'free';
      } else {
        app.price += ' EUR';
      }

      return app;
    },
    getApps() {
      this.isLoading = true;
      const filter = { ...this.filter };

      return this.$api.getRequests(this.page, filter)
        .then((data) => {
          if (!isEqual(filter, this.filter)) {
            return;
          }

          data = data.map(this.mapRequest);

          this.apps = [...this.apps, ...data];
          this.isLoading = false;
          if (data.length < 25) {
            this.isLastPage = true;
          }
        });
    },
    async tryFulfill(request) {
      this.fulfillingRequest = null;
      await this.$nextTick();
      this.fulfillingRequest = request;
      this.$modal.show('fulfillRequestFromUniversalSearch');
    },
    onFulfilled(request) {
      this.$notify({
        type: 'success',
        text: this.$t('REQUEST_FULFILLED_SUCCESSFULLY'),
      });

      const idx = this.apps.findIndex(app => app.id === request.id);
      if (idx !== -1) {
        this.apps.splice(idx, 1, this.mapRequest(request));
      }
    },
    requestOld(app) {
      if (app.am_i_requested) {
        return;
      }

      app.am_i_requested = 1;
      app.requesters_amount -= -1;
      this.$api.requestApp(`/app/id${app.trackid}`)
        .catch(() => {
          app.requested = false;
          app.am_i_requested = 0;
          app.requesters_amount -= 1;
        });
    },
    requestNew() {
      this.$modal.show('request');
      this.$nextTick()
        .then(() => this.$refs.requestUrlInput.focus());
    },
    request() {
      if (this.requesting) {
        return;
      }

      this.requesting = true;
      this.$api.requestApp(this.requestUrl, this.requestVersion)
        .then(() => {
          this.$modal.hide('request');
          this.$notify({
            type: 'success',
            text: this.$t('APP_REQUESTED_SUCCESSFULLY'),
          });

          this.apps = [];
          this.page = 1;
          this.getApps();
        })
        .finally(() => {
          this.requesting = false;
        });
    },
    clearRequestPopup() {
      this.requestUrl = '';
      this.requestVersion = '';
      this.isRequestVersionInputVisible = false;
    },
    // eslint-disable-next-line
    handleScroll: throttle(function () {
      if (this.isLoading || this.isLastPage || !this.$refs.app) {
        return;
      }

      const index = this.apps.length - 20;
      const breakpoint = this.$refs.app[index >= 0 ? index : 0].$el.offsetTop;

      if (window.scrollY > breakpoint) {
        this.page += 1;
        this.getApps();
      }
    }, 1000),
  },
  serverPrefetch() {
    return this.getApps();
  },
  created() {
    this.getApps();
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  @include notebook {
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 767px) {
    align-items: stretch;
  }
}

.heading {
  display: block;
  width: 100%;
  color: var(--text-tertiary);
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;

  @include notebook {
    text-align: center;
  }

  @media (max-width: 767px) {
    text-align: left;
  }
}

.filters {
  width: 180px;

  @include notebook {
    width: auto;
    margin-bottom: 10px;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
}

.apps {
  margin: 0 20px;
  flex-grow: 1;

  @include notebook {
    margin: 30px 0 0;
    order: 1;
  }
}

.notifications {
  flex: 0 0 300px;

  @include notebook {
    flex: 0 0 auto;
    order: 0;
  }
}

.notification {
  background-image: var(--pink-gradient);
  border: 1px solid var(--pink-border);
  border-radius: 10px;
  padding: 15px;
  margin: 0;
  color: var(--text-primary);

  &__heading {
    font-size: 20px;
    color: var(--text-tertiary);
    font-weight: 600;
    margin-bottom: 10px;
  }

  &__text {
    line-height: 20px;
  }

  &__link {
    display: inline-block;
    background: var(--bg);
    border: 1px solid $primary;
    border-radius: 2px;
    color: $primary;
    padding: 7px 10px;
    margin-top: 10px;
  }
}

.request-modal {
  .request-modal__checkbox {
    margin-top: 10px;

    & + * {
      margin-top: 10px;
    }
  }
}
</style>
